<template>
  <v-card flat border class="filters-card pa-4 mb-6">
    <div class="d-flex align-center mb-4">
      <v-icon size="22" class="me-3">mdi-filter-outline</v-icon>
      <h2 class="text-subtitle-1 font-weight-medium">Filtros</h2>
    </div>

    <v-row align="center" class="filters-row">
      <v-col cols="12" md="3">
        <v-text-field v-model="localFilters.search" label="Buscar atividades" prepend-inner-icon="mdi-magnify"
          variant="outlined" density="compact" hide-details clearable @input="debouncedSearch" />
      </v-col>

      <v-col cols="12" sm="6" md="2">
        <v-select v-model="localFilters.status" :items="statusOptions" label="Status" variant="outlined"
          density="compact" hide-details clearable @update:model-value="updateFilter('status', $event)">
          <template #selection="{ item }">
            <v-chip :color="getStatusColor(item.value)" size="small" variant="flat">
              {{ item.title }}
            </v-chip>
          </template>

          <template #item="{ props, item }">
            <v-list-item v-bind="props">
              <template #prepend>
                <v-icon :color="getStatusColor(item.value)" size="20">
                  {{ getStatusIcon(item.value) }}
                </v-icon>
              </template>
            </v-list-item>
          </template>
        </v-select>
      </v-col>

      <v-col cols="12" sm="6" md="2">
        <v-select v-model="localFilters.dateRange" :items="dateRangeOptions" item-title="title" item-value="value"
          label="Vencimento" variant="outlined" density="compact" hide-details clearable
          @update:model-value="updateFilter('dateRange', $event)">
          <template #selection="{ item }">
            <div class="d-flex align-center">
              <v-icon :color="item.raw?.color" size="16" class="mr-2">
                {{ item.raw?.icon }}
              </v-icon>
              {{ item.title }}
            </div>
          </template>

          <template #item="{ props, item }">
            <v-list-item v-bind="props">
              <template #prepend>
                <v-icon :color="item.raw?.color" size="20">
                  {{ item.raw?.icon }}
                </v-icon>
              </template>
            </v-list-item>
          </template>
        </v-select>
      </v-col>

      <v-col cols="12" sm="6" md="2">
        <v-select v-model="localFilters.assignee" :items="assigneeOptions" label="Responsável" variant="outlined"
          density="compact" hide-details clearable @update:model-value="updateFilter('assignee', $event)">
          <template #selection="{ item }">
            <div class="d-flex align-center">
              <v-avatar size="20" class="mr-2">
                <v-img v-if="item.raw?.avatar" :src="item.raw.avatar" :alt="item.title" />
                <span v-else class="text-caption">{{ getInitials(item.title) }}</span>
              </v-avatar>
              {{ item.title }}
            </div>
          </template>

          <template #item="{ props, item }">
            <v-list-item v-bind="props">
              <template #prepend>
                <v-avatar size="24">
                  <v-img v-if="item.raw?.avatar" :src="item.raw.avatar" :alt="item.title" />
                  <span v-else class="text-caption">{{ getInitials(item.title) }}</span>
                </v-avatar>
              </template>
            </v-list-item>
          </template>
        </v-select>
      </v-col>

      <v-col cols="12" sm="6" md="2" class="d-flex align-center">
        <v-btn v-if="hasActiveFilters" variant="outlined" color="error" prepend-icon="mdi-filter-off"
          @click="clearAllFilters">
          Limpar
        </v-btn>
      </v-col>
    </v-row>

    <!-- Chips de filtros ativos -->
    <v-row v-if="activeFilterChips.length > 0" class="mt-2">
      <v-col cols="12">
        <div class="d-flex flex-wrap gap-2 align-center">
          <span class="text-caption text-medium-emphasis">Filtros ativos:</span>
          <v-chip v-for="chip in activeFilterChips" :key="chip.key" :color="chip.color" size="small" variant="tonal"
            closable @click:close="removeFilter(chip.key)">
            <v-icon v-if="chip.icon" size="14" class="mr-1">{{ chip.icon }}</v-icon>
            {{ chip.label }}
          </v-chip>
        </div>
      </v-col>
    </v-row>
  </v-card>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useActivityFormatters } from '@/composables/useActivities.js'
import { debounce } from 'lodash-es'

const props = defineProps({
  modelValue: {
    type: Object,
    default: () => ({})
  },
  assignees: {
    type: Array,
    default: () => []
  }
})

const emit = defineEmits(['update:modelValue', 'filtersChanged'])

const { getStatusColor, getPriorityColor } = useActivityFormatters()

const localFilters = ref({
  search: '',
  status: null,
  assignee: null,
  dateRange: null,
  priority: null,
  ...props.modelValue
})

const statusOptions = [
  { title: 'Não Iniciada', value: 'not_started' },
  { title: 'Em Andamento', value: 'in_progress' },
  { title: 'Concluída', value: 'completed' }
]

const dateRangeOptions = [
  {
    title: 'Próximos',
    value: 'today',
    icon: 'mdi-calendar-today',
    color: 'primary'
  },
  {
    title: 'Esta Semana',
    value: 'this_week',
    icon: 'mdi-calendar-week',
    color: 'info'
  },
  {
    title: 'Este Mês',
    value: 'this_month',
    icon: 'mdi-calendar-month',
    color: 'success'
  },
  {
    title: 'Próximos 7 dias',
    value: 'next_7_days',
    icon: 'mdi-calendar-arrow-right',
    color: 'warning'
  },
  {
    title: 'Vencidas',
    value: 'overdue',
    icon: 'mdi-calendar-alert',
    color: 'error'
  }
]

const priorityOptions = [
  { title: 'Baixa', value: 'low' },
  { title: 'Média', value: 'medium' },
  { title: 'Alta', value: 'high' },
  { title: 'Urgente', value: 'urgent' }
]

const assigneeOptions = computed(() => {
  return props.assignees
    .map(assignee => ({
      title: assignee.name || assignee,
      value: assignee.id || assignee,
      avatar: assignee.avatar
    }))
    .sort((a, b) => {
      const nameA = a.title.toLowerCase()
      const nameB = b.title.toLowerCase()
      return nameA.localeCompare(nameB, 'pt-BR')
    })
})

const hasActiveFilters = computed(() => {
  return Object.values(localFilters.value).some(value =>
    value !== null && value !== '' && value !== undefined
  )
})

const activeFilterChips = computed(() => {
  const chips = []

  if (localFilters.value.search) {
    chips.push({
      key: 'search',
      label: `"${localFilters.value.search}"`,
      icon: 'mdi-magnify',
      color: 'primary'
    })
  }

  if (localFilters.value.status) {
    const status = statusOptions.find(s => s.value === localFilters.value.status)
    chips.push({
      key: 'status',
      label: status?.title || localFilters.value.status,
      icon: getStatusIcon(localFilters.value.status),
      color: getStatusColor(localFilters.value.status)
    })
  }

  if (localFilters.value.assignee) {
    const assignee = assigneeOptions.value.find(a => a.value === localFilters.value.assignee)
    chips.push({
      key: 'assignee',
      label: assignee?.title || localFilters.value.assignee,
      icon: 'mdi-account',
      color: 'info'
    })
  }

  if (localFilters.value.dateRange) {
    const range = dateRangeOptions.find(r => r.value === localFilters.value.dateRange)
    chips.push({
      key: 'dateRange',
      label: range?.title || localFilters.value.dateRange,
      icon: range?.icon || 'mdi-calendar',
      color: range?.color || 'primary'
    })
  }

  if (localFilters.value.priority) {
    const priority = priorityOptions.find(p => p.value === localFilters.value.priority)
    chips.push({
      key: 'priority',
      label: priority?.title || localFilters.value.priority,
      icon: getPriorityIcon(localFilters.value.priority),
      color: getPriorityColor(localFilters.value.priority)
    })
  }

  return chips
})

const updateFilter = (key, value) => {
  localFilters.value[key] = value
  emitFilters()
}

const removeFilter = (key) => {
  localFilters.value[key] = null
  emitFilters()
}

const clearAllFilters = () => {
  localFilters.value = {
    search: '',
    status: null,
    assignee: null,
    dateRange: null,
    priority: null
  }
  emitFilters()
}

const emitFilters = () => {
  emit('update:modelValue', { ...localFilters.value })
  emit('filtersChanged', { ...localFilters.value })
}

const debouncedSearch = debounce(() => {
  updateFilter('search', localFilters.value.search)
}, 300)

const getStatusIcon = (status) => {
  const icons = {
    'not_started': 'mdi-circle-outline',
    'in_progress': 'mdi-clock',
    'completed': 'mdi-check-circle',
    'overdue': 'mdi-alert-circle'
  }
  return icons[status] || 'mdi-circle'
}

const getPriorityIcon = (priority) => {
  const icons = {
    'low': 'mdi-arrow-down',
    'medium': 'mdi-minus',
    'high': 'mdi-arrow-up',
    'urgent': 'mdi-fire'
  }
  return icons[priority] || 'mdi-minus'
}

const getInitials = (name) => {
  if (!name) return '?'
  return name.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2)
}

watch(() => props.modelValue, (newValue) => {
  localFilters.value = { ...newValue }
}, { deep: true })
</script>

<style scoped>
.filters-card {
  background: rgba(var(--v-theme-surface), 0.8);
  backdrop-filter: blur(10px);
}

.filters-row {
  gap: 8px;
}

@media (max-width: 960px) {
  .filters-row {
    gap: 16px;
  }
}

.v-chip {
  transition: all 0.2s ease;
}
</style>
